import getConfig from 'next/config';

/**
 * Utility functions for calculating vertex product offset values
 * Used for analytics tracking of product positions in search results and product listings
 */

const { publicRuntimeConfig } = getConfig();

const DEFAULT_PAGE_SIZE: number = publicRuntimeConfig.pageSize;
const DEFAULT_CHUNK_SIZE: number = publicRuntimeConfig.chunkSize;

/**
 * Calculates the product offset for a given page number
 * @param page - The page number (1-based)
 * @param pageSize - The number of products per page
 * @returns The product offset (0-based)
 */
export const calculateProductOffsetByPage = (
  page: number | string,
  pageSize: number = DEFAULT_PAGE_SIZE
): number => {
  const validatedPage = validatePage(page);
  const validatedPageSize = validatePageSize(pageSize);

  return validatedPageSize * (validatedPage - 1);
};

/**
 * Calculates the product offset for a given page and chunk
 * @param page - The page number (1-based)
 * @param chunk - The chunk number (1-based)
 * @param chunkSize - The number of products per chunk
 * @param pageSize - The number of products per page
 * @returns The product offset (0-based)
 */
export const calculateProductOffsetByChunk = (
  page: number | string,
  chunk: number | string,
  chunkSize: number = DEFAULT_CHUNK_SIZE,
  pageSize: number = DEFAULT_PAGE_SIZE
): number => {
  const validatedPage = validatePage(page);
  const validatedChunk = validateChunk(chunk);
  const validatedChunkSize = validateChunkSize(chunkSize);
  const validatedPageSize = validatePageSize(pageSize);

  const pageOffset = validatedPageSize * (validatedPage - 1);
  const chunkOffset = validatedChunkSize * (validatedChunk - 1);

  return pageOffset + chunkOffset;
};

/**
 * Validates and sanitizes an offset value (integer, non-negative)
 * @param offset - The offset value to validate
 * @returns A valid offset (>= 0), or 0 if invalid
 */
export const validateOffset = (offset: unknown): number => {
  if (offset == null) {
    return 0;
  }

  const numericOffset = Number(offset);

  if (isNaN(numericOffset) || !isFinite(numericOffset)) {
    return 0;
  }

  const flooredOffset = Math.floor(numericOffset);
  return Math.max(0, flooredOffset);
};

/**
 * Validates and sanitizes a page number
 * @param page - The page number to validate
 * @returns A valid page number (>= 1)
 */
const validatePage = (page: unknown): number => {
  const numericPage = Number(page);
  if (isNaN(numericPage) || numericPage < 1) {
    return 1;
  }
  return Math.floor(numericPage);
};

/**
 * Validates and sanitizes a chunk number
 * @param chunk - The chunk number to validate
 * @returns A valid chunk number (>= 1)
 */
const validateChunk = (chunk: unknown): number => {
  const numericChunk = Number(chunk);
  if (isNaN(numericChunk) || numericChunk < 1) {
    return 1;
  }
  return Math.floor(numericChunk);
};

/**
 * Validates and sanitizes a page size
 * @param pageSize - The page size to validate
 * @returns A valid page size (>= 1)
 */
const validatePageSize = (pageSize: unknown): number => {
  const numericPageSize = Number(pageSize);
  if (isNaN(numericPageSize) || numericPageSize < 1) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.floor(numericPageSize);
};

/**
 * Validates and sanitizes a chunk size
 * @param chunkSize - The chunk size to validate
 * @returns A valid chunk size (>= 1)
 */
const validateChunkSize = (chunkSize: unknown): number => {
  const numericChunkSize = Number(chunkSize);
  if (isNaN(numericChunkSize) || numericChunkSize < 1) {
    return DEFAULT_CHUNK_SIZE;
  }
  return Math.floor(numericChunkSize);
};
